import { useNavigate } from "react-router";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useSwitchOrganization } from "@/lib/use-switch-organization";
import { recarregarNaOrganizacao } from "@/lib/recarregar-na-organizacao";

export type NotificacaoAbrivel = {
  link: string | null;
  organization?: { id: string; name: string; types: string[]; isPersonal: boolean } | null;
};

/**
 * Abre o destino de uma notificação — inclusive quando ela é de outra organização.
 *
 * O link de uma notificação só faz sentido dentro da organização que a gerou ("/lab/pedidos" na
 * organização do laboratório). Se ela for de outra, troca primeiro e recarrega já no destino —
 * recarga, e não navegação, pelo mesmo motivo do seletor de organizações: estado de componente da
 * organização anterior não pode sobreviver à troca.
 *
 * Devolve `true` quando navegou na organização atual (quem chamou pode fechar o popover); `false`
 * quando a troca está em andamento e a página vai recarregar sozinha.
 */
export function useAbrirNotificacao() {
  const navigate = useNavigate();
  const { switchTo } = useSwitchOrganization();
  const meQuery = trpc.me.current.useQuery();
  const orgAtivaId = meQuery.data?.activeOrganization?.id ?? null;

  const ehDeOutraOrganizacao = (n: NotificacaoAbrivel) => !!n.organization && n.organization.id !== orgAtivaId;

  const abrir = (n: NotificacaoAbrivel): boolean => {
    if (!n.link) return false;
    if (ehDeOutraOrganizacao(n) && n.organization) {
      const org = n.organization;
      switchTo(org.id)
        .then(() => recarregarNaOrganizacao({ types: org.types as never, isPersonal: org.isPersonal }, n.link!))
        .catch((e) => toast.error(e.message || "Não foi possível trocar de organização."));
      return false;
    }
    navigate(n.link);
    return true;
  };

  return { abrir, ehDeOutraOrganizacao };
}
